import { useCallback, useMemo, useState, FC, Fragment } from 'react';
import { observer } from 'mobx-react';
import { tokens } from '@servicetitan/tokens/core';
import { BodyText, Popover, Stack, StatusLight } from '@servicetitan/design-system';

import { useClientRect } from '../../../utils/use-client-rect';
import { radiusRelative } from '../utils/const';
import { PieChartPopoverContentType, PiePiece } from '../utils/interface';

const chartPadding = 8;
const px = (value?: number) => `${value || 0}px`;

const PiePiece: FC<{
    piece: PiePiece;
    selected?: boolean;
}> = ({ piece: { id, color, opacity, points, text, path }, selected }) =>
    points && path ? (
        <g id={id}>
            {!!selected && (
                <path
                    d={path}
                    fill={tokens.colorWhite}
                    stroke={tokens.colorBlue200}
                    strokeOpacity="50%"
                    strokeWidth="3"
                    paintOrder="stroke"
                />
            )}
            <path d={path} fill={color} />

            <svg
                x={points[4][0] - 10}
                y={points[4][1] - (points[4][1] > 0 ? 3 : 1)}
                width={20}
                height={6}
            >
                <text
                    x="50%"
                    y="50%"
                    fontSize={4}
                    fontWeight={600}
                    fill={opacity > 0.3 ? tokens.colorWhite : tokens.colorBlack}
                    dominantBaseline="middle"
                    textAnchor="middle"
                >
                    {text}
                </text>
            </svg>
        </g>
    ) : null;

const PiePieceHover: FC<{
    piece: PiePiece;
    onMouse(id: string, isEnter: boolean): void;
}> = ({ piece, onMouse }) => {
    const onMouseEnter = useCallback(() => onMouse(piece.id, true), [onMouse, piece.id]);
    const onMouseLeave = useCallback(() => onMouse(piece.id, false), [onMouse, piece.id]);

    return (
        <path
            d={piece.path}
            fill="white"
            opacity="0"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        />
    );
};

const PieSvg: FC<{
    pieces: PiePiece[];
    selectedIndex: number;
}> = ({ pieces, selectedIndex }) => (
    <svg
        className="position-absolute"
        style={{ inset: px(chartPadding) }}
        viewBox={
            `-${radiusRelative} -${radiusRelative} ` + `${radiusRelative * 2} ${radiusRelative * 2}`
        }
    >
        {pieces.map((p, index) =>
            p.path ? <PiePiece piece={p} key={p.id} selected={index === selectedIndex} /> : null
        )}

        {selectedIndex >= 0 && selectedIndex < pieces.length && (
            <use xlinkHref={pieces[selectedIndex].id} />
        )}
    </svg>
);

const PieSvgHover: FC<{
    pieces: PiePiece[];
    onMouse(id: string, isEnter: boolean): void;
}> = ({ pieces, onMouse }) => (
    <svg
        className="position-absolute z-global-nav"
        style={{ inset: px(chartPadding) }}
        viewBox={
            `-${radiusRelative} -${radiusRelative} ` + `${radiusRelative * 2} ${radiusRelative * 2}`
        }
    >
        {pieces.map(p =>
            p.path ? <PiePieceHover piece={p} key={p.id} onMouse={onMouse} /> : null
        )}
    </svg>
);

export const Pie: FC<{
    title: string;
    pieces: PiePiece[];
    popoverContent?: PieChartPopoverContentType;
}> = observer(({ pieces, title, popoverContent: PopoverContent }) => {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [rect, ref] = useClientRect();

    const onMouse = useCallback(
        (id: string, isEnter: boolean) => {
            setSelectedIndex(isEnter ? pieces.findIndex(p => p.id === id) : -1);
        },
        [pieces, setSelectedIndex]
    );

    const container = useMemo(() => {
        const height = rect?.height || 0;

        return {
            height,
            internal: height ? height - chartPadding * 2 : 0,
            styles: height
                ? {
                      width: px(Math.max(250, height)),
                      overflow: 'hidden',
                  }
                : {},
        };
    }, [rect]);

    const triggersStyles = useMemo(
        () =>
            container.height
                ? pieces.map(p =>
                      p.points
                          ? {
                                top: px(
                                    (container.height * (radiusRelative + p.points[4][1])) /
                                        (radiusRelative * 2)
                                ),
                                left: px(
                                    (container.height * (radiusRelative + p.points[4][0])) /
                                        (radiusRelative * 2) +
                                        20
                                ),
                            }
                          : { top: '', left: '' }
                  )
                : [],
        [pieces, container]
    );

    return (
        <div ref={ref} style={container.styles} className="position-relative">
            {pieces.every(p => !p.path) ? (
                <Stack className="h-100" justifyContent="center" alignItems="center">
                    No Data
                </Stack>
            ) : (
                <Fragment>
                    {triggersStyles
                        .filter(ts => !!ts.left && !!ts.top)
                        .map((ts, ind) => (
                            <div key={ts.left + ts.top} style={ts} className="position-absolute">
                                <Popover
                                    portal
                                    trigger={<span> </span>}
                                    open={selectedIndex === ind}
                                    direction="rb"
                                    padding="s"
                                    width="auto"
                                >
                                    {selectedIndex === ind && (
                                        <Stack direction="column">
                                            <Stack alignItems="center">
                                                <StatusLight color={pieces[ind].color} />
                                                <BodyText size="small" bold>
                                                    {title}: {pieces[ind].title}
                                                </BodyText>
                                            </Stack>
                                            {!!PopoverContent && (
                                                <Stack.Item className="m-l-1">
                                                    <PopoverContent
                                                        index={ind}
                                                        data={pieces[ind]?.data}
                                                    />
                                                </Stack.Item>
                                            )}
                                        </Stack>
                                    )}
                                </Popover>
                            </div>
                        ))}

                    <PieSvg pieces={pieces} selectedIndex={selectedIndex} />
                    <PieSvgHover pieces={pieces} onMouse={onMouse} />
                </Fragment>
            )}
        </div>
    );
});
